import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { message } from 'antd';
import type { AxiosError } from 'axios';
import { serviceApi } from '@api/serviceApi';
import type {
  InstalledEquipmentFilters,
  CreateInstalledEquipmentRequest,
  UpdateInstalledEquipmentRequest,
  ServiceRequestFilters,
  CreateServiceRequestRequest,
  UpdateServiceRequestRequest,
  AddServicePartRequest,
} from '@/types/service';

// Сервер повертає причину відмови в полі error (напр. «є відкриті заявки») — показуємо її
function mdServerErrorText(mdError: unknown, mdFallback: string): string {
  const mdText = (mdError as AxiosError<{ error?: string }>)?.response?.data?.error;
  return mdText ? `${mdFallback}: ${mdText}` : mdFallback;
}

// ── Installed Equipment ─────────────────────────────────

export function useInstalledEquipment(mdFilters: InstalledEquipmentFilters) {
  return useQuery({
    queryKey: ['service', 'equipment', mdFilters],
    queryFn: () => serviceApi.getInstalledEquipment(mdFilters),
    placeholderData: (mdPreviousData) => mdPreviousData,
  });
}

export function useInstalledEquipmentById(mdId: string | undefined) {
  return useQuery({
    queryKey: ['service', 'equipment', mdId],
    queryFn: () => serviceApi.getInstalledEquipmentById(mdId!),
    enabled: !!mdId,
  });
}

export function useCreateInstalledEquipment() {
  const mdQueryClient = useQueryClient();

  return useMutation({
    mutationFn: (mdRequest: CreateInstalledEquipmentRequest) =>
      serviceApi.createInstalledEquipment(mdRequest),
    onSuccess: () => {
      mdQueryClient.invalidateQueries({ queryKey: ['service', 'equipment'] });
      message.success('Обладнання додано');
    },
    onError: () => {
      message.error('Помилка при додаванні обладнання');
    },
  });
}

export function useUpdateInstalledEquipment() {
  const mdQueryClient = useQueryClient();

  return useMutation({
    mutationFn: (mdRequest: UpdateInstalledEquipmentRequest) =>
      serviceApi.updateInstalledEquipment(mdRequest),
    onSuccess: () => {
      mdQueryClient.invalidateQueries({ queryKey: ['service', 'equipment'] });
      message.success('Обладнання оновлено');
    },
    onError: () => {
      message.error('Помилка при оновленні обладнання');
    },
  });
}

export function useDeleteInstalledEquipment() {
  const mdQueryClient = useQueryClient();

  return useMutation({
    mutationFn: (mdId: string) => serviceApi.deleteInstalledEquipment(mdId),
    onSuccess: () => {
      mdQueryClient.invalidateQueries({ queryKey: ['service', 'equipment'] });
      message.success('Обладнання видалено');
    },
    onError: (mdError) => {
      message.error(mdServerErrorText(mdError, 'Помилка при видаленні обладнання'));
    },
  });
}

// ── Service Requests ──────────────────────────────────────

export function useServiceRequests(mdFilters: ServiceRequestFilters) {
  return useQuery({
    queryKey: ['service', 'requests', mdFilters],
    queryFn: () => serviceApi.getServiceRequests(mdFilters),
    placeholderData: (mdPreviousData) => mdPreviousData,
  });
}

export function useServiceRequestById(mdId: string | undefined) {
  return useQuery({
    queryKey: ['service', 'requests', mdId],
    queryFn: () => serviceApi.getServiceRequestById(mdId!),
    enabled: !!mdId,
  });
}

export function useCreateServiceRequest() {
  const mdQueryClient = useQueryClient();

  return useMutation({
    mutationFn: (mdRequest: CreateServiceRequestRequest) =>
      serviceApi.createServiceRequest(mdRequest),
    onSuccess: () => {
      mdQueryClient.invalidateQueries({ queryKey: ['service', 'requests'] });
      message.success('Сервісну заявку створено');
    },
    onError: () => {
      message.error('Помилка при створенні заявки');
    },
  });
}

export function useUpdateServiceRequest() {
  const mdQueryClient = useQueryClient();

  return useMutation({
    mutationFn: (mdRequest: UpdateServiceRequestRequest) =>
      serviceApi.updateServiceRequest(mdRequest),
    onSuccess: () => {
      mdQueryClient.invalidateQueries({ queryKey: ['service', 'requests'] });
      message.success('Заявку оновлено');
    },
    onError: () => {
      message.error('Помилка при оновленні заявки');
    },
  });
}

export function useAssignServiceRequest() {
  const mdQueryClient = useQueryClient();

  return useMutation({
    mutationFn: (mdParams: { id: string; engineerId: string }) =>
      serviceApi.assignServiceRequest(mdParams.id, mdParams.engineerId),
    onSuccess: () => {
      mdQueryClient.invalidateQueries({ queryKey: ['service', 'requests'] });
      message.success('Інженера призначено');
    },
    onError: (mdError) => {
      message.error(mdServerErrorText(mdError, 'Помилка при призначенні інженера'));
    },
  });
}

export function useStartServiceRequest() {
  const mdQueryClient = useQueryClient();

  return useMutation({
    mutationFn: (mdId: string) => serviceApi.startServiceRequest(mdId),
    onSuccess: () => {
      mdQueryClient.invalidateQueries({ queryKey: ['service', 'requests'] });
      message.success('Роботу розпочато');
    },
    onError: () => {
      message.error('Помилка при зміні статусу');
    },
  });
}

export function useCompleteServiceRequest() {
  const mdQueryClient = useQueryClient();

  return useMutation({
    mutationFn: (mdParams: { id: string; resolution: string }) =>
      serviceApi.completeServiceRequest(mdParams.id, mdParams.resolution),
    onSuccess: () => {
      mdQueryClient.invalidateQueries({ queryKey: ['service', 'requests'] });
      message.success('Заявку завершено');
    },
    onError: () => {
      message.error('Помилка при завершенні заявки');
    },
  });
}

export function useCloseServiceRequest() {
  const mdQueryClient = useQueryClient();

  return useMutation({
    mutationFn: (mdId: string) => serviceApi.closeServiceRequest(mdId),
    onSuccess: () => {
      mdQueryClient.invalidateQueries({ queryKey: ['service', 'requests'] });
      message.success('Заявку закрито');
    },
    onError: () => {
      message.error('Помилка при закритті заявки');
    },
  });
}

export function useAddServicePart() {
  const mdQueryClient = useQueryClient();

  return useMutation({
    mutationFn: (mdParams: { requestId: string; part: AddServicePartRequest }) =>
      serviceApi.addServicePart(mdParams.requestId, mdParams.part),
    onSuccess: () => {
      mdQueryClient.invalidateQueries({ queryKey: ['service', 'requests'] });
      // Вартість сервісу обладнання змінюється разом із запчастинами
      mdQueryClient.invalidateQueries({ queryKey: ['service', 'equipment'] });
      message.success('Запчастину додано');
    },
    onError: () => {
      message.error('Помилка при додаванні запчастини');
    },
  });
}

export function useRemoveServicePart() {
  const mdQueryClient = useQueryClient();

  return useMutation({
    mutationFn: (mdParams: { requestId: string; partId: string }) =>
      serviceApi.removeServicePart(mdParams.requestId, mdParams.partId),
    onSuccess: () => {
      mdQueryClient.invalidateQueries({ queryKey: ['service', 'requests'] });
      mdQueryClient.invalidateQueries({ queryKey: ['service', 'equipment'] });
      message.success('Запчастину видалено');
    },
    onError: (mdError) => {
      message.error(mdServerErrorText(mdError, 'Помилка при видаленні запчастини'));
    },
  });
}

export function useServiceEngineers() {
  return useQuery({
    queryKey: ['service', 'engineers'],
    queryFn: () => serviceApi.getServiceEngineers(),
    staleTime: 5 * 60 * 1000,
  });
}

// ── SLA Dashboard ─────────────────────────────────────────

export function useSlaSummary() {
  return useQuery({
    queryKey: ['service', 'sla-summary'],
    queryFn: () => serviceApi.getSlaSummary(),
  });
}
